/**
 * IMPORTANT - BLOCKCHAIN DATA INTEGRITY:
 * This file handles blockchain data which must maintain exact values and case sensitivity.
 * - NEVER use toLowerCase() or toUpperCase()
 * - NEVER normalize strings or addresses
 * - NEVER transform collection names
 * - NEVER modify contract addresses
 * - ALWAYS use exact matches for comparisons
 * - ALWAYS preserve original data as it exists on-chain
 */

import { getApiConfig } from './reservoir';

/**
 * Fetch historical floor prices for a collection
 * @param {string} collectionName - The collection name or address
 * @returns {Promise<Object|null>} - Historical price data or null if not found
 */
export async function fetchHistoricalFloorPrices(collectionName) {
  try {
    if (!collectionName) {
      throw new Error('Collection name is required');
    }

    console.log(`[Debug] Fetching historical prices for ${collectionName}`);

    // Get API configuration
    const { apiKey, baseUrl } = getApiConfig();
    
    // Fetch historical floor prices from Reservoir - use exact collection name
    const response = await fetch(`${baseUrl}/collections/v2/${encodeURIComponent(collectionName)}/floor-ask/v2`, {
      headers: {
        'x-api-key': apiKey
      }
    });

    if (!response.ok) {
      throw new Error(`API returned ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();
    
    if (!data.collections?.[0]?.floorAsk?.price) {
      console.log(`[Debug] No floor price data found for ${collectionName}`);
      return null;
    }

    // Convert price data to our format
    const priceData = {
      usd: new Map(),
      native: new Map()
    };

    // Add current floor price
    const currentPrice = data.collections[0].floorAsk.price;
    const timestamp = Date.now();
    
    priceData.usd.set(timestamp, currentPrice.amount.usd);
    priceData.native.set(timestamp, currentPrice.amount.native);

    console.log(`[Debug] Fetched current floor price for ${collectionName}:`, {
      usd: currentPrice.amount.usd,
      native: currentPrice.amount.native
    });

    return priceData;
  } catch (error) {
    console.error('[Debug] Error fetching historical prices:', error);
    return null;
  }
}

export async function findClosestHistoricalPrice(collectionName, targetDate) {
    const prices = await fetchHistoricalFloorPrices(collectionName);
    if (!prices || !prices.usd.size) return null;
    
    const targetDateStr = targetDate.toISOString().split('T')[0];
    
    // First try exact match
    if (prices.usd.has(targetDateStr)) {
        return {
            date: targetDateStr,
            price: prices.usd.get(targetDateStr),
            native: prices.native.get(targetDateStr)
        };
    }
    
    // Find closest date
    const dates = Array.from(prices.usd.keys()).map(d => new Date(d));
    let closestDate = dates[0];
    let minDiff = Math.abs(dates[0] - targetDate);
    
    for (const date of dates) {
        const diff = Math.abs(date - targetDate);
        if (diff < minDiff) {
            minDiff = diff;
            closestDate = date;
        }
    }
    
    const closestDateStr = closestDate.toISOString().split('T')[0];
    
    return {
        date: closestDateStr,
        price: prices.usd.get(closestDateStr),
        native: prices.native.get(closestDateStr)
    };
}

export async function fetchFloorPrice(collectionAddress) {
  try {
    const collection = await fetchCollectionId(collectionAddress);
    
    if (!collection.floorAsk) {
      throw new Error('No floor price available');
    }

    const floorPrice = collection.floorAsk.price.amount.decimal;
    return floorPrice;
  } catch (error) {
    console.error('Error fetching floor price:', error);
    throw error;
  }
} 